import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { auth, billing, apiKeys as apiKeysApi, type PricingPlan } from "@/lib/api";
import { TRACKING_CNAME_TARGET } from "@/lib/siteConfig";
import {
  User, Lock, Mail, Link, CreditCard, Trash2, Key, Send,
  CheckCircle, XCircle, Loader2, X, Eye, EyeOff, Check, ExternalLink, Copy, Plus,
} from "lucide-react";

// ── Toast ─────────────────────────────────────────────────────────────────────
function Toast({ msg, type, onClose }: { msg: string; type: "ok"|"err"; onClose: () => void }) {
  return (
    <div className={`fixed bottom-6 right-4 sm:right-6 z-50 flex items-center gap-3 px-4 py-3 rounded-2xl shadow-xl text-sm font-semibold max-w-xs sm:max-w-sm
      ${type === "ok" ? "bg-ink text-white" : "bg-red-500 text-white"}`}>
      {type === "ok" ? <Check size={16} /> : <X size={16} />}
      <span className="flex-1">{msg}</span>
      <button onClick={onClose} className="opacity-60 hover:opacity-100 ml-2"><X size={14} /></button>
    </div>
  );
}

// ── Section wrapper ───────────────────────────────────────────────────────────
function Section({ id, title, icon: Icon, children }: { id: string; title: string; icon: React.ElementType; children: React.ReactNode }) {
  return (
    <div id={id} className="bg-white rounded-2xl border border-black/[0.06] overflow-hidden">
      <div className="flex items-center gap-3 px-5 sm:px-6 py-4 border-b border-black/[0.06]">
        <div className="p-2 rounded-xl bg-black/[0.03]"><Icon size={16} className="text-slate" /></div>
        <h2 className="font-bold text-ink">{title}</h2>
      </div>
      <div className="px-5 sm:px-6 py-5 sm:py-6">{children}</div>
    </div>
  );
}

// ── Field ─────────────────────────────────────────────────────────────────────
function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs font-bold uppercase tracking-wider text-slate mb-1.5">{label}</label>
      {children}
    </div>
  );
}

const inp = "w-full px-4 py-2.5 rounded-xl border border-black/[0.08] bg-paper text-sm focus:outline-none focus:ring-2 focus:ring-coral/30 focus:border-coral/50 transition-all";
const btn = (variant: "primary"|"secondary"|"danger") => ({
  primary:   "px-5 py-2.5 rounded-full bg-coral text-white text-sm font-semibold hover:bg-coral-dark disabled:opacity-50 transition-colors shadow-sm shadow-coral/20",
  secondary: "px-5 py-2.5 rounded-full border border-black/[0.08] text-ink text-sm font-semibold hover:border-black/20 disabled:opacity-50 transition-colors",
  danger:    "px-5 py-2.5 rounded-full bg-red-500 text-white text-sm font-semibold hover:bg-red-600 disabled:opacity-50 transition-colors",
}[variant]);

// ── Tracking domain card ──────────────────────────────────────────────────────
function TrackingDomainSection({ onMsg }: { onMsg: (m: string, t: "ok"|"err") => void }) {
  const [domain, setDomain]         = useState("");
  const [saved, setSaved]           = useState<string | null>(null);
  const [loading, setLoading]       = useState(true);
  const [saving, setSaving]         = useState(false);

  useEffect(() => {
    auth.getTrackingDomain()
      .then((r) => { setSaved(r.tracking_domain); setDomain(r.tracking_domain || ""); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const v = domain.trim().toLowerCase() || null;
      const r = await auth.setTrackingDomain(v);
      setSaved(r.tracking_domain); setDomain(r.tracking_domain || "");
      onMsg("Tracking domain saved", "ok");
    } catch (e: any) { onMsg(e.message || "Failed to save", "err"); }
    finally { setSaving(false); }
  };

  const clear = async () => {
    setSaving(true);
    try {
      await auth.setTrackingDomain(null); setSaved(null); setDomain("");
      onMsg("Tracking domain removed", "ok");
    } catch (e: any) { onMsg(e.message || "Failed to clear", "err"); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="flex items-center gap-2 text-slate text-sm py-2"><Loader2 size={14} className="animate-spin" /> Loading…</div>;

  return (
    <div className="space-y-4">
      {saved ? (
        <div className="flex items-center gap-3 p-3 rounded-xl bg-green-50 border border-green-200 text-sm text-green-700">
          <CheckCircle size={15} className="shrink-0" />
          <span>Active: <span className="font-mono font-semibold">{saved}</span></span>
          <button onClick={clear} disabled={saving} className="ml-auto p-1 rounded hover:bg-green-100 disabled:opacity-50"><X size={14} /></button>
        </div>
      ) : (
        <div className="flex items-center gap-3 p-3 rounded-xl bg-black/[0.03] border border-black/[0.06] text-sm text-slate">
          <XCircle size={15} className="opacity-40 shrink-0" />
          Not configured — using MoreMail's default tracking domain
        </div>
      )}
      <Field label="Your subdomain">
        <input type="text" value={domain} onChange={(e) => setDomain(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && save()}
          placeholder="track.yourdomain.com" className={inp} />
      </Field>
      <button onClick={save} disabled={saving || domain.trim() === (saved || "")} className={btn("primary")}>
        {saving ? "Saving…" : "Save domain"}
      </button>
      <div className="rounded-xl bg-black/[0.02] border border-black/[0.06] p-4">
        <p className="text-xs font-bold text-slate uppercase tracking-wider mb-3">DNS setup</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
          {[
            { label: "Type",            value: "CNAME" },
            { label: "Name / Host",     value: domain || "track" },
            { label: "Points to",       value: TRACKING_CNAME_TARGET },
          ].map(({ label, value }) => (
            <div key={label} className="bg-white rounded-lg border border-black/[0.06] p-3">
              <p className="text-slate font-semibold mb-1">{label}</p>
              <p className="font-mono text-ink break-all">{value}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function SunsetSection({ onMsg }: { onMsg: (m: string, t: "ok"|"err") => void }) {
  const [enabled, setEnabled]   = useState(0);
  const [days, setDays]         = useState(90);
  const [lastRun, setLastRun]   = useState<string | null>(null);
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);

  useEffect(() => {
    auth.getSunsetSettings()
      .then((r) => { setEnabled(r.sunset_enabled); setDays(r.sunset_days); setLastRun(r.sunset_last_run_at); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const r = await auth.setSunsetSettings(enabled, days);
      setEnabled(r.sunset_enabled);
      setDays(r.sunset_days);
      onMsg("Sunset settings saved", "ok");
    } catch (e: any) {
      onMsg(e.message || "Failed to save", "err");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-sm text-slate">Loading...</div>;

  return (
    <div className="space-y-4">
      <p className="text-sm text-slate">
        Automatically unsubscribe contacts who haven't opened any email in a while.
        This improves deliverability and keeps your list healthy.
      </p>
      <label className="flex items-center justify-between cursor-pointer">
        <div>
          <p className="text-sm font-semibold text-ink">Enable auto-sunset</p>
          <p className="text-xs text-slate">Inactive subscribers will be quietly unsubscribed</p>
        </div>
        <input
          type="checkbox"
          checked={!!enabled}
          onChange={(e) => setEnabled(e.target.checked ? 1 : 0)}
          className="w-5 h-5 rounded border-black/20 text-coral focus:ring-coral/30"
        />
      </label>
      {!!enabled && (
        <div>
          <label className="block text-xs font-bold uppercase tracking-wider text-slate mb-1.5">
            Unsubscribe after
          </label>
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="w-full px-4 py-2.5 rounded-lg border border-black/[0.08] bg-paper text-sm focus:outline-none focus:ring-2 focus:ring-coral/30"
          >
            <option value={30}>30 days of no opens</option>
            <option value={60}>60 days of no opens</option>
            <option value={90}>90 days of no opens</option>
            <option value={120}>120 days of no opens</option>
            <option value={180}>180 days of no opens</option>
            <option value={365}>365 days of no opens</option>
          </select>
          {lastRun && (
            <p className="text-[11px] text-slate mt-2">
              Last run: {new Date(lastRun).toLocaleDateString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
            </p>
          )}
        </div>
      )}
      <button
        onClick={handleSave}
        disabled={saving}
        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-coral text-white text-sm font-semibold hover:bg-coral-dark disabled:opacity-50 transition-colors"
      >
        {saving ? <><Loader2 size={14} className="animate-spin" /> Saving...</> : "Save Settings"}
      </button>
    </div>
  );
}

// ── Custom SMTP section ─────────────────────────────────────────────────────
function CustomSmtpSection({ onMsg }: { onMsg: (m: string, t: "ok"|"err") => void }) {
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);
  const [testing, setTesting]     = useState(false);
  const [removing, setRemoving]   = useState(false);
  const [configured, setConfigured] = useState(false);
  const [showForm, setShowForm]   = useState(false);
  const [showPw, setShowPw]       = useState(false);

  // Form fields
  const [host, setHost]           = useState("");
  const [port, setPort]           = useState(587);
  const [secure, setSecure]       = useState(false);
  const [user, setUser]           = useState("");
  const [password, setPassword]   = useState("");
  const [fromName, setFromName]   = useState("");
  const [fromAddr, setFromAddr]   = useState("");

  useEffect(() => {
    auth.getCustomSmtp()
      .then((r) => {
        if (r.configured && r.smtp) {
          setConfigured(true);
          setHost(r.smtp.host);
          setPort(r.smtp.port);
          setSecure(r.smtp.secure);
          setUser(r.smtp.user);
          setFromName(r.smtp.from_name);
          setFromAddr(r.smtp.from_address);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    if (!host.trim() || !user.trim() || !password.trim() || !fromAddr.trim()) {
      onMsg("Please fill in all required fields", "err");
      return;
    }
    setSaving(true);
    try {
      await auth.saveCustomSmtp({
        host: host.trim(),
        port,
        secure,
        user: user.trim(),
        password,
        from_name: fromName.trim() || undefined,
        from_address: fromAddr.trim(),
      });
      setConfigured(true);
      setShowForm(false);
      setPassword("");
      onMsg("Custom SMTP saved and verified", "ok");
    } catch (e: any) {
      onMsg(e.message || "Failed to save SMTP settings", "err");
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      const r = await auth.testCustomSmtp();
      onMsg(r.message || "Test email sent!", "ok");
    } catch (e: any) {
      onMsg(e.message || "Test email failed", "err");
    } finally {
      setTesting(false);
    }
  };

  const handleRemove = async () => {
    if (!confirm("Remove your custom SMTP? Campaigns will use MoreMail's default SMTP.")) return;
    setRemoving(true);
    try {
      await auth.deleteCustomSmtp();
      setConfigured(false);
      setHost(""); setPort(587); setSecure(false);
      setUser(""); setPassword(""); setFromName(""); setFromAddr("");
      onMsg("Custom SMTP removed", "ok");
    } catch (e: any) {
      onMsg(e.message || "Failed to remove", "err");
    } finally {
      setRemoving(false);
    }
  };

  if (loading) return <div className="flex items-center gap-2 text-slate text-sm py-2"><Loader2 size={14} className="animate-spin" /> Loading…</div>;

  return (
    <div className="space-y-4">
      {configured && !showForm ? (
        <>
          <div className="flex items-center gap-3 p-3 rounded-xl bg-green-50 border border-green-200 text-sm text-green-700">
            <CheckCircle size={15} className="shrink-0" />
            <div className="flex-1 min-w-0">
              <span className="font-semibold">Connected</span>
              <span className="mx-1.5">·</span>
              <span className="font-mono text-xs">{host}:{port}</span>
              <span className="mx-1.5">·</span>
              <span className="text-xs">{fromName ? `${fromName} <${fromAddr}>` : fromAddr}</span>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={handleTest} disabled={testing} className={btn("secondary")}>
              {testing ? <><Loader2 size={14} className="animate-spin inline mr-1.5" />Sending…</> : "Send test email"}
            </button>
            <button onClick={() => { setShowForm(true); setPassword(""); }} className={btn("secondary")}>
              Edit settings
            </button>
            <button onClick={handleRemove} disabled={removing} className={btn("danger")}>
              {removing ? "Removing…" : "Remove"}
            </button>
          </div>
        </>
      ) : (
        <>
          {!configured && (
            <div className="flex items-center gap-3 p-3 rounded-xl bg-black/[0.03] border border-black/[0.06] text-sm text-slate">
              <XCircle size={15} className="opacity-40 shrink-0" />
              Not configured — using MoreMail's default SMTP
            </div>
          )}

          <div className="space-y-4 p-4 rounded-xl border border-black/[0.06] bg-black/[0.01]">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="SMTP Host *">
                <input value={host} onChange={(e) => setHost(e.target.value)} className={inp}
                  placeholder="smtp.gmail.com" />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Port *">
                  <select value={port} onChange={(e) => { const p = Number(e.target.value); setPort(p); setSecure(p === 465); }}
                    className={inp}>
                    <option value={587}>587 (STARTTLS)</option>
                    <option value={465}>465 (SSL)</option>
                    <option value={25}>25 (Plain)</option>
                    <option value={2525}>2525 (Alt)</option>
                  </select>
                </Field>
                <Field label="Encryption">
                  <select value={secure ? "ssl" : "starttls"} onChange={(e) => setSecure(e.target.value === "ssl")}
                    className={inp}>
                    <option value="starttls">STARTTLS</option>
                    <option value="ssl">SSL/TLS</option>
                  </select>
                </Field>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Username / Email *">
                <input value={user} onChange={(e) => setUser(e.target.value)} className={inp}
                  placeholder="you@example.com" />
              </Field>
              <Field label={configured ? "Password * (re-enter to update)" : "Password *"}>
                <div className="relative">
                  <input type={showPw ? "text" : "password"} value={password}
                    onChange={(e) => setPassword(e.target.value)} className={`${inp} pr-10`}
                    placeholder="••••••••" />
                  <button type="button" onClick={() => setShowPw(!showPw)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate hover:text-ink">
                    {showPw ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
              </Field>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="From Name">
                <input value={fromName} onChange={(e) => setFromName(e.target.value)} className={inp}
                  placeholder="My Company" />
              </Field>
              <Field label="From Email Address *">
                <input value={fromAddr} onChange={(e) => setFromAddr(e.target.value)} className={inp}
                  placeholder="hello@yourdomain.com" />
              </Field>
            </div>

            <div className="flex gap-2 pt-1">
              <button onClick={handleSave} disabled={saving} className={btn("primary")}>
                {saving ? <><Loader2 size={14} className="animate-spin inline mr-1.5" />Verifying…</> : "Save & Verify"}
              </button>
              {showForm && configured && (
                <button onClick={() => setShowForm(false)} className={btn("secondary")}>Cancel</button>
              )}
            </div>
          </div>

          <div className="rounded-xl bg-black/[0.02] border border-black/[0.06] p-4">
            <p className="text-xs font-bold text-slate uppercase tracking-wider mb-2">Common SMTP Providers</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs">
              {[
                { name: "Gmail / Google Workspace", host: "smtp.gmail.com", port: 587 },
                { name: "Outlook / Microsoft 365", host: "smtp.office365.com", port: 587 },
                { name: "Amazon SES", host: "email-smtp.us-east-1.amazonaws.com", port: 587 },
              ].map(({ name, host: h, port: p }) => (
                <button key={h} onClick={() => { setHost(h); setPort(p); setSecure(p === 465); }}
                  className="text-left bg-white rounded-lg border border-black/[0.06] p-2.5 hover:border-coral/30 transition-colors">
                  <p className="font-semibold text-ink">{name}</p>
                  <p className="text-slate font-mono mt-0.5">{h}:{p}</p>
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────
// ── API Keys section ─────────────────────────────────────────────────────────
function ApiKeysSection({ onMsg }: { onMsg: (m: string, t: "ok"|"err") => void }) {
  const [keys, setKeys] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [newKey, setNewKey] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const load = async () => {
    try {
      const r = await apiKeysApi.list();
      setKeys(r.keys || []);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    try {
      const r = await apiKeysApi.create(newName.trim());
      setNewKey(r.key.raw_key);
      setNewName("");
      onMsg("API key created — copy it now, it won't be shown again", "ok");
      load();
    } catch (e: any) {
      onMsg(e.message || "Failed to create key", "err");
    }
    setCreating(false);
  };

  const revoke = async (id: string) => {
    if (!confirm("Revoke this API key? Any integration using it will stop working.")) return;
    try {
      await apiKeysApi.revoke(id);
      onMsg("API key revoked", "ok");
      load();
    } catch (e: any) {
      onMsg(e.message || "Failed to revoke", "err");
    }
  };

  const copyKey = () => {
    if (newKey) {
      navigator.clipboard.writeText(newKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (loading) return <div className="flex items-center gap-2 text-slate text-sm py-2"><Loader2 size={14} className="animate-spin" /> Loading…</div>;

  return (
    <div className="space-y-4">
      <p className="text-sm text-slate">
        Use API keys to integrate MoreMail with external tools. Keys authenticate requests to <span className="font-mono text-xs bg-black/[0.04] px-1.5 py-0.5 rounded">/api/v1/*</span> endpoints.
      </p>

      {/* New key reveal */}
      {newKey && (
        <div className="p-4 rounded-xl bg-green-50 border border-green-200 space-y-2">
          <p className="text-sm font-semibold text-green-800">Your new API key (shown once):</p>
          <div className="flex items-center gap-2">
            <code className="flex-1 px-3 py-2 bg-white rounded-lg border border-green-200 text-sm font-mono text-green-900 break-all select-all">{newKey}</code>
            <button onClick={copyKey} className="shrink-0 px-3 py-2 rounded-lg bg-green-600 text-white text-sm font-semibold hover:bg-green-700 flex items-center gap-1.5">
              {copied ? <><Check size={14} /> Copied</> : <><Copy size={14} /> Copy</>}
            </button>
          </div>
          <button onClick={() => setNewKey(null)} className="text-xs text-green-600 hover:underline">Dismiss</button>
        </div>
      )}

      {/* Create */}
      <div className="flex gap-2">
        <input
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && create()}
          placeholder="Key name (e.g. autoresearch)"
          className={inp}
        />
        <button onClick={create} disabled={creating || !newName.trim()} className={`${btn("primary")} shrink-0 flex items-center gap-1.5`}>
          <Plus size={14} /> {creating ? "Creating…" : "Create Key"}
        </button>
      </div>

      {/* List */}
      {keys.length > 0 ? (
        <div className="divide-y divide-black/[0.06] border border-black/[0.06] rounded-xl overflow-hidden">
          {keys.map((k) => (
            <div key={k.id} className="flex items-center justify-between px-4 py-3 bg-white hover:bg-black/[0.01]">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-ink truncate">{k.name}</p>
                <p className="text-xs text-slate font-mono">{k.key_prefix}…</p>
                <p className="text-[10px] text-slate/60 mt-0.5">
                  Created {new Date(k.created_at).toLocaleDateString()}
                  {k.last_used_at && <> · Last used {new Date(k.last_used_at).toLocaleDateString()}</>}
                  {k.expires_at && <> · Expires {new Date(k.expires_at).toLocaleDateString()}</>}
                </p>
              </div>
              <button
                onClick={() => revoke(k.id)}
                disabled={!k.is_active}
                className={`shrink-0 ml-3 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                  k.is_active
                    ? "text-red-600 border border-red-200 hover:bg-red-50"
                    : "text-slate/40 border border-black/[0.06] cursor-default"
                }`}
              >
                {k.is_active ? "Revoke" : "Revoked"}
              </button>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-slate/60 italic">No API keys yet.</p>
      )}
    </div>
  );
}

export default function SettingsPage() {
  const { user, login } = useAuth();

  // Profile
  const [name, setName]       = useState(user?.name    || "");
  const [company, setCompany] = useState(user?.company || "");
  const [savingProfile, setSavingProfile] = useState(false);

  // Password
  const [curPw, setCurPw]   = useState("");
  const [newPw, setNewPw]   = useState("");
  const [confPw, setConfPw] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [savingPw, setSavingPw] = useState(false);

  // Delete
  const [deleteInput, setDeleteInput] = useState("");
  const [showDelete, setShowDelete]   = useState(false);
  const [deleting, setDeleting]       = useState(false);

  // Toast
  const [toast, setToast] = useState<{ msg: string; type: "ok"|"err" } | null>(null);
  const showToast = (msg: string, type: "ok"|"err" = "ok") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      await auth.updateProfile({ name: name.trim(), company: company.trim() });
      showToast("Profile updated");
    } catch (e: any) { showToast(e.message || "Failed to save", "err"); }
    finally { setSavingProfile(false); }
  };

  const savePassword = async () => {
    if (newPw !== confPw) { showToast("Passwords don't match", "err"); return; }
    if (newPw.length < 8)  { showToast("Password must be at least 8 characters", "err"); return; }
    setSavingPw(true);
    try {
      await auth.changePassword(curPw, newPw);
      setCurPw(""); setNewPw(""); setConfPw("");
      showToast("Password changed");
    } catch (e: any) { showToast(e.message || "Failed to change password", "err"); }
    finally { setSavingPw(false); }
  };

  const [currentPlan, setCurrentPlan] = useState<PricingPlan | null>(null);
  const [allPlans, setAllPlans] = useState<PricingPlan[]>([]);
  const [hasBilling, setHasBilling] = useState(false);
  const [billingLoading, setBillingLoading] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const checkoutSuccess = searchParams.get("checkout") === "success";

  useEffect(() => {
    billing.plans().then((d) => {
      setAllPlans(d.plans);
      const match = d.plans.find((p) => p.slug === (user?.plan || "starter"));
      if (match) setCurrentPlan(match);
    }).catch(console.error);
    billing.subscription().then((d) => setHasBilling(d.has_billing)).catch(() => {});
  }, [user?.plan]);

  const subsLabel = currentPlan?.subscriber_limit ? `${currentPlan.subscriber_limit.toLocaleString()} subscribers` : "Unlimited";
  const emailsLabel = currentPlan?.monthly_email_limit ? `${currentPlan.monthly_email_limit.toLocaleString()} emails/mo` : "Unlimited emails";
  const sentPct  = Math.min(100, Math.round(
    ((user?.emails_sent_this_month || 0) / (user?.monthly_email_limit || 15000)) * 100
  ));

  const handleUpgrade = async (slug: string) => {
    setBillingLoading(true);
    try {
      const { url } = await billing.checkout(slug, "month");
      window.location.href = url;
    } catch (e: any) { showToast(e.message || "Checkout failed", "err"); }
    finally { setBillingLoading(false); }
  };

  const handlePortal = async () => {
    setBillingLoading(true);
    try {
      const { url } = await billing.portal();
      window.location.href = url;
    } catch (e: any) { showToast(e.message || "Could not open billing portal", "err"); }
    finally { setBillingLoading(false); }
  };

  return (
    <div>
      <div className="sticky top-0 z-10 bg-paper/90 backdrop-blur-md border-b border-black/[0.06] px-4 sm:px-6 lg:px-8 py-4">
        <h1 className="text-lg sm:text-xl font-bold text-ink">Settings</h1>
        <p className="text-xs sm:text-sm text-slate mt-0.5">Manage your account and preferences</p>
      </div>

      <div className="p-4 sm:p-6 lg:p-8 max-w-2xl mx-auto space-y-5">

        {checkoutSuccess && (
          <div className="bg-teal/10 border border-teal/20 text-teal rounded-2xl px-5 py-3 text-sm flex items-center justify-between">
            <span>Your plan has been upgraded successfully!</span>
            <button onClick={() => setSearchParams({})} className="text-teal/60 hover:text-teal ml-3"><X size={14} /></button>
          </div>
        )}

        {/* Profile */}
        <Section id="profile" title="Profile" icon={User}>
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Full name">
                <input value={name} onChange={(e) => setName(e.target.value)} className={inp} placeholder="Jane Smith" />
              </Field>
              <Field label="Company">
                <input value={company} onChange={(e) => setCompany(e.target.value)} className={inp} placeholder="Acme Inc." />
              </Field>
            </div>
            <Field label="Email address">
              <input value={user?.email || ""} readOnly className={`${inp} opacity-60 cursor-default`} />
            </Field>
            <div className="flex justify-end pt-1">
              <button onClick={saveProfile} disabled={savingProfile} className={btn("primary")}>
                {savingProfile ? "Saving…" : "Save profile"}
              </button>
            </div>
          </div>
        </Section>

        {/* Password */}
        <Section id="password" title="Password" icon={Lock}>
          <div className="space-y-4">
            <Field label="Current password">
              <div className="relative">
                <input type={showPw ? "text" : "password"} value={curPw}
                  onChange={(e) => setCurPw(e.target.value)} className={`${inp} pr-10`} placeholder="••••••••" />
                <button type="button" onClick={() => setShowPw(!showPw)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate hover:text-ink">
                  {showPw ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
            </Field>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="New password">
                <input type={showPw ? "text" : "password"} value={newPw}
                  onChange={(e) => setNewPw(e.target.value)} className={inp} placeholder="8+ characters" />
              </Field>
              <Field label="Confirm new password">
                <input type={showPw ? "text" : "password"} value={confPw}
                  onChange={(e) => setConfPw(e.target.value)} className={inp} placeholder="Repeat password" />
              </Field>
            </div>
            {newPw && confPw && newPw !== confPw && (
              <p className="text-xs text-red-500">Passwords don't match</p>
            )}
            <div className="flex justify-end pt-1">
              <button onClick={savePassword} disabled={savingPw || !curPw || !newPw || !confPw} className={btn("primary")}>
                {savingPw ? "Changing…" : "Change password"}
              </button>
            </div>
          </div>
        </Section>

        {/* Tracking domain */}
        <Section id="tracking" title="Custom Tracking Domain" icon={Link}>
          <p className="text-sm text-slate mb-5">
            Use your own subdomain for open and click tracking links to avoid shared-domain spam penalties.
          </p>
          <TrackingDomainSection onMsg={showToast} />
        </Section>

        {/* Custom SMTP */}
        <Section id="smtp" title="Email Sending (SMTP)" icon={Send}>
          <p className="text-sm text-slate mb-5">
            Connect your own SMTP server to send campaigns from your own email infrastructure.
            Your credentials are encrypted and verified before saving.
          </p>
          <CustomSmtpSection onMsg={showToast} />
        </Section>

        {/* Sunset / List Hygiene */}
        <Section id="sunset" title="List Hygiene (Sunset)" icon={Trash2}>
          <SunsetSection onMsg={showToast} />
        </Section>

        {/* Plan & Billing */}
        <Section id="plan" title="Plan & Billing" icon={CreditCard}>
          <div className="space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <span className="px-3 py-1.5 bg-coral/10 text-coral text-sm font-bold rounded-full capitalize">
                  {currentPlan?.name || user?.plan || "Starter"}
                </span>
                <span className="text-sm text-slate ml-3">{subsLabel}</span>
              </div>
              {hasBilling ? (
                <button onClick={handlePortal} disabled={billingLoading}
                  className="inline-flex items-center gap-1.5 text-sm font-semibold text-coral hover:underline disabled:opacity-50">
                  Manage billing <ExternalLink size={13} />
                </button>
              ) : (
                <a href="/pricing" className="text-sm font-semibold text-coral hover:underline">
                  Upgrade plan →
                </a>
              )}
            </div>
            <div>
              <div className="flex justify-between text-xs text-slate mb-1.5">
                <span>Emails this month</span>
                <span className="font-semibold">
                  {(user?.emails_sent_this_month || 0).toLocaleString()} / {(user?.monthly_email_limit || 15000).toLocaleString()}
                </span>
              </div>
              <div className="h-2 bg-black/[0.05] rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${sentPct > 85 ? "bg-amber-500" : "bg-teal"}`}
                  style={{ width: `${sentPct}%` }}
                />
              </div>
              <p className="text-xs text-slate mt-1">{emailsLabel}</p>
            </div>

            {/* Quick upgrade buttons for non-current paid plans */}
            {allPlans.filter((p) => p.slug !== (user?.plan || "starter") && p.monthly_price && p.monthly_price > 0).length > 0 && (
              <div className="pt-2 border-t border-black/[0.06]">
                <p className="text-xs font-semibold text-slate uppercase tracking-wider mb-3">Upgrade</p>
                <div className="flex flex-wrap gap-2">
                  {allPlans
                    .filter((p) => p.slug !== (user?.plan || "starter") && p.monthly_price && p.monthly_price > 0)
                    .map((p) => (
                      <button key={p.slug} onClick={() => handleUpgrade(p.slug)} disabled={billingLoading}
                        className="px-4 py-2 rounded-full border border-coral/30 text-coral text-sm font-semibold hover:bg-coral/5 disabled:opacity-50 transition-colors">
                        {p.name} — ${p.monthly_price}/mo
                      </button>
                    ))}
                </div>
              </div>
            )}
          </div>
        </Section>

        {/* API Keys */}
        <Section id="api-keys" title="API Keys" icon={Key}>
          <ApiKeysSection onMsg={showToast} />
        </Section>

        {/* Danger zone */}
        <Section id="danger" title="Danger Zone" icon={Trash2}>
          <p className="text-sm text-slate mb-4">
            Permanently delete your account and all data. This cannot be undone.
          </p>
          {!showDelete ? (
            <button onClick={() => setShowDelete(true)} className={btn("danger")}>
              Delete account
            </button>
          ) : (
            <div className="space-y-3 p-4 rounded-xl border border-red-200 bg-red-50">
              <p className="text-sm font-semibold text-red-700">
                Type <span className="font-mono bg-red-100 px-1.5 py-0.5 rounded">delete my account</span> to confirm
              </p>
              <input value={deleteInput} onChange={(e) => setDeleteInput(e.target.value)}
                className="w-full px-4 py-2.5 rounded-xl border border-red-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-red-300"
                placeholder="delete my account" />
              <div className="flex gap-3">
                <button onClick={() => { setShowDelete(false); setDeleteInput(""); }}
                  className={btn("secondary")}>Cancel</button>
                <button disabled={deleteInput !== "delete my account" || deleting}
                  className={btn("danger")}>
                  {deleting ? "Deleting…" : "Delete my account"}
                </button>
              </div>
            </div>
          )}
        </Section>
      </div>

      {toast && <Toast msg={toast.msg} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
}
